/**
 * Steps Section — auto-progression, progress bar, pause/play, click-to-navigate
 */
if (!customElements.get('csk-steps')) {
  class CskSteps extends HTMLElement {
    connectedCallback() {
      this._steps = this.querySelectorAll('[data-csk-step]');
      this._track = this.querySelector('[data-csk-steps-track]');
      this._progressSteps = this.querySelectorAll('[data-csk-progress-step]');
      this._pauseBtn = this.querySelector('[data-csk-steps-pause]');
      this._playBtn = this.querySelector('[data-csk-steps-play]');
      this._prevBtn = this.querySelector('[data-csk-steps-prev]');
      this._nextBtn = this.querySelector('[data-csk-steps-next]');
      this._videos = this.querySelectorAll('.csk-steps__video');

      this._stepCount = this._steps.length;
      if (this._stepCount === 0) return;

      this._secondsPerStep = parseInt(this.dataset.secondsPerStep) || 10;
      this._currentStep = 0;
      this._isPaused = false;
      this._timeout = null;

      this._bindEvents();
      this._observeVisibility();
    }

    disconnectedCallback() {
      this._clearTimeout();
      if (this._observer) this._observer.disconnect();
    }

    _bindEvents() {
      this._pauseBtn?.addEventListener('click', () => this._pause());
      this._playBtn?.addEventListener('click', () => this._start());

      this._prevBtn?.addEventListener('click', () => {
        if (this._currentStep > 0) {
          this._pause();
          this._goToStep(this._currentStep - 1);
        }
      });

      this._nextBtn?.addEventListener('click', () => {
        if (this._currentStep < this._stepCount - 1) {
          this._pause();
          this._goToStep(this._currentStep + 1);
        }
      });

      this._steps.forEach((step, i) => {
        step.addEventListener('click', () => {
          this._pause();
          this._goToStep(i);
        });
      });

      /* Sync on manual scroll snap */
      let scrollEndTimer;
      this._track?.addEventListener('scroll', () => {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => this._syncStepFromScroll(), 120);
      }, { passive: true });
    }

    _observeVisibility() {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this._isPaused) {
              this._start();
            } else {
              this._clearTimeout();
              this._resetAllBars();
            }
          });
        },
        { threshold: 0.5 }
      );
      if (this._track) this._observer.observe(this._track);
    }

    _goToStep(index) {
      this._currentStep = index;

      /* Scroll step into view */
      const step = this._steps[index];
      step.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

      /* Play/pause videos */
      this._videos.forEach((v) => v.pause());
      const video = step.querySelector('video');
      if (video) video.play().catch(() => {});

      /* Update progress indicators */
      this._progressSteps.forEach((ps, i) => {
        const bar = ps.querySelector('.csk-steps__progress-bar');
        bar.style.transitionDuration = '0s';
        bar.style.width = '0%';
        ps.classList.toggle('csk-steps__progress-step--current', i === index);
      });

      /* Mark current step */
      this._steps.forEach((s, i) => {
        s.classList.toggle('csk-steps__step--current', i === index);
      });

      this._updateArrows(index);
    }

    _updateArrows(index) {
      if (this._prevBtn) this._prevBtn.disabled = index === 0;
      if (this._nextBtn) this._nextBtn.disabled = index === this._stepCount - 1;
    }

    _startCurrentBar() {
      const ps = this._progressSteps[this._currentStep];
      if (!ps) return;

      const bar = ps.querySelector('.csk-steps__progress-bar');
      bar.style.transitionDuration = `${this._secondsPerStep}s`;
      bar.style.width = '100%';

      this._clearTimeout();

      if (this._currentStep === this._stepCount - 1) {
        /* Last step: pause at the end */
        this._timeout = setTimeout(() => this._pause(), this._secondsPerStep * 1000);
      } else {
        this._timeout = setTimeout(() => this._nextStep(), this._secondsPerStep * 1000);
      }
    }

    _nextStep() {
      const next = (this._currentStep + 1) % this._stepCount;
      this._goToStep(next);
      if (!this._isPaused) this._startCurrentBar();
    }

    _pause() {
      this._isPaused = true;
      this._clearTimeout();
      this._resetAllBars();

      this._pauseBtn?.classList.add('hidden');
      this._playBtn?.classList.remove('hidden');
    }

    _start() {
      this._isPaused = false;

      if (this._currentStep === this._stepCount - 1) {
        this._goToStep(0);
      }

      this._startCurrentBar();

      this._pauseBtn?.classList.remove('hidden');
      this._playBtn?.classList.add('hidden');
    }

    _resetAllBars() {
      this._progressSteps.forEach((ps) => {
        const bar = ps.querySelector('.csk-steps__progress-bar');
        bar.style.transitionDuration = '0s';
        bar.style.width = '0%';
      });
    }

    _clearTimeout() {
      if (this._timeout) {
        clearTimeout(this._timeout);
        this._timeout = null;
      }
    }

    _syncStepFromScroll() {
      if (!this._track) return;
      const trackLeft = this._track.getBoundingClientRect().left;

      let closestIndex = 0;
      let closestDist = Infinity;

      this._steps.forEach((step, i) => {
        const rect = step.getBoundingClientRect();
        const center = (rect.left + rect.right) / 2 - trackLeft;
        const dist = Math.abs(center - this._track.clientWidth / 2);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = i;
        }
      });

      if (closestIndex !== this._currentStep) {
        this._currentStep = closestIndex;

        this._steps.forEach((s, i) => s.classList.toggle('csk-steps__step--current', i === closestIndex));
        this._progressSteps.forEach((ps, i) => {
          const bar = ps.querySelector('.csk-steps__progress-bar');
          bar.style.transitionDuration = '0s';
          bar.style.width = '0%';
          ps.classList.toggle('csk-steps__progress-step--current', i === closestIndex);
        });

        this._videos.forEach((v) => v.pause());
        const video = this._steps[closestIndex].querySelector('video');
        if (video) video.play().catch(() => {});

        this._updateArrows(closestIndex);
      }
    }
  }

  customElements.define('csk-steps', CskSteps);
}
